/**
 * The place a provider pick offers an appointment's Where block (LP-26),
 * shared by the record form and the confirm card so the two resolve the
 * same pick to the same place.
 *
 * A provider's place is its office: the care-team row's own address
 * parts, else the parts of the Directory entry it links, else the
 * entry's address as fetched (parts, or text only). A Directory pick is
 * fetched the same way. A provider in the Directory with no address
 * still offers its place, name and link (the link is the valuable part;
 * the address can be typed in the boxes, which then show empty). A
 * care-team row with neither an address nor a Directory link offers no
 * place, and the host leaves Where as it is.
 *
 * The place's name is the organization's name, or "<name>'s office" for
 * a person. A care-team row not linked to the Directory may be either,
 * so it keeps its own name.
 */
import { api } from '@/lib/api';
import type { ProviderRow } from '@/lib/clientRecord';
import type { CareTeamPick } from '@/components/clientRecord/CareTeamCombobox';
import type { WhereDirectoryEntry } from '@/components/clientRecord/AppointmentWhere';

export const PLACE_PART_KEYS = ['line1', 'line2', 'city', 'state', 'zip'] as const;
export type PlacePartKey = (typeof PLACE_PART_KEYS)[number];
/** The five parts as the boxes hold them ('' for an empty part). */
export type PlaceParts = Record<PlacePartKey, string>;

/** The address a Directory entry carries: parts when it has a street,
 *  else its text, else neither. */
export interface EntryAddress {
  parts: PlaceParts | null;
  text: string | null;
}

/** A place a provider pick offers Where. */
export interface ProviderPlace {
  /** The place line: the organization, or the person's office. */
  name: string;
  /** The Directory entry that travels as the place's id, if any. */
  entry: WhereDirectoryEntry | null;
  parts: PlaceParts | null;
  /** An entry whose address is text only. */
  text: string | null;
}

/** The place name for a provider: "<name>'s office" for a person, the
 *  name itself for an organization or a provider of unknown kind. */
export function placeNameFor(name: string, kind: 'individual' | 'organization' | null): string {
  const n = name.trim();
  return kind === 'individual' ? `${n}'s office` : n;
}

/** Parts from the record's nullable shape, or null with no street. */
export function placePartsOf(
  p: { line1?: string | null; line2?: string | null; city?: string | null; state?: string | null; zip?: string | null } | null | undefined,
): PlaceParts | null {
  if (p === null || p === undefined) return null;
  const line1 = (p.line1 ?? '').trim();
  if (line1 === '') return null;
  return { line1, line2: p.line2 ?? '', city: p.city ?? '', state: p.state ?? '', zip: p.zip ?? '' };
}

/** A Directory entry's address, fetched; a failed read is no address. */
export async function entryAddress(tenantSlug: string, entry: Pick<WhereDirectoryEntry, 'id' | 'type'>): Promise<EntryAddress> {
  try {
    const d = entry.type === 'individual' ? await api.getIndividual(tenantSlug, entry.id) : await api.getProvider(tenantSlug, entry.id);
    const parts = placePartsOf({
      line1: d.address_line1,
      line2: d.address_line2,
      city: d.address_city,
      state: d.address_state,
      zip: d.address_zip,
    });
    if (parts !== null) return { parts, text: null };
    const text = typeof d.address === 'string' && d.address.trim() !== '' ? d.address : null;
    return { parts: null, text };
  } catch {
    return { parts: null, text: null };
  }
}

/** The place a provider pick offers, or null for a care-team row with
 *  neither an address nor a Directory link. */
export async function providerPlace(
  tenantSlug: string,
  pick: CareTeamPick,
  value: string,
  careTeam: readonly ProviderRow[],
): Promise<ProviderPlace | null> {
  if (pick.kind === 'directory') {
    const name = placeNameFor(value, pick.type);
    const entry: WhereDirectoryEntry = { id: pick.id, name, type: pick.type };
    return { name, entry, ...(await entryAddress(tenantSlug, entry)) };
  }
  const row = careTeam.find((r) => r.id === pick.providerId);
  if (row === undefined) return null;
  const type = row.link.kind === 'org' ? 'organization' : row.link.kind === 'individual' ? 'individual' : null;
  const name = placeNameFor(row.displayName, type);
  const entry: WhereDirectoryEntry | null =
    type !== null && (row.link.kind === 'org' || row.link.kind === 'individual') ? { id: row.link.id, name, type } : null;
  const own = placePartsOf(row.officeAddressParts ?? row.directoryAddressParts);
  if (own !== null) return { name, entry, parts: own, text: null };
  if (entry === null) return null;
  return { name, entry, ...(await entryAddress(tenantSlug, entry)) };
}

/** True when Where already holds this place: the same name, and the same
 *  Directory entry (or none on both). */
export function holdsPlace(
  where: { name: string; entry: Pick<WhereDirectoryEntry, 'id' | 'type'> | null },
  place: ProviderPlace,
): boolean {
  if (where.name.trim() !== place.name.trim()) return false;
  if (where.entry === null || place.entry === null) return where.entry === place.entry;
  return where.entry.id === place.entry.id && where.entry.type === place.entry.type;
}
